// Legacy catch-all route — the unmodified renderPage() switch from the old
// src/App.jsx, now living behind a splat route so it coexists with real file
// routes as they're added one module at a time (see docs/plans/toasty-soaring-pudding.md).
// TanStack Router ranks exact/dynamic routes above a splat, so the moment e.g.
// events.$id.tsx exists, /events/:id resolves there instead of here — nothing
// in this file needs to change when that happens; just delete the matching
// case + import + ROUTE_TABLE row.
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { parsePathname, pathForRoute, cacheEntity, readCachedEntity, entityId } from '../lib/router';
import { getRouteEntity, resolveRouteEntity } from '../lib/queries/entityById';
import { invalidateRequestsList } from '../lib/queries/requests';
import { addEventRequestNote, updateEventRequestStatus } from '../lib/backOfficeApi';
import { toast } from '../lib/buses';
import { MOCK } from '../data/mock';
import { useShell } from '../lib/shellContext';
import { DetailNotFound, RouteNotFound } from '../components/DetailNotFound';
import { AccessControlPage } from '../modules/accesscontrol/AccessControlPage';
import { DesignPage } from '../modules/comms/DesignPage';
import { DashboardPage } from '../modules/dashboard/DashboardPage';
import { EventRequestReviewPageV2 } from '../modules/eventrequests/EventRequestReviewPageV2';
import { EventRequestsPage } from '../modules/eventrequests/EventRequestsPage';
import { TicketingInventoryPage } from '../modules/events/inventory/TicketingInventoryPage';
import { OrderDetailPage } from '../modules/events/orders/OrderDetailPage';
import { OrdersPage } from '../modules/events/orders/OrdersPage';
import { PaymentPlansPage } from '../modules/paymentplans/PaymentPlansPage';
import { ArrangementDetailPage } from '../modules/paymentplans/ArrangementDetailPage';
import { EventPaymentPlansPage } from '../modules/paymentplans/EventPaymentPlansPage';
import { ChannelsPage } from '../modules/finance/ChannelsPage';
import { FinancePage } from '../modules/finance/FinancePage';
import { TransactionDetailPage } from '../modules/finance/TransactionDetailPage';
import { TransactionsPageV2 } from '../modules/finance/TransactionsPageV2';
import { WalletsPage } from '../modules/finance/WalletsPage';
import { PayoutDetailPage } from '../modules/finance/payouts/PayoutDetailPage';
import { PayoutsPageV2 } from '../modules/finance/payouts/PayoutsPageV2';
import { FundingReviewPanel } from '../modules/finance/FundingReviewPanel';
import { ReconciliationPage } from '../modules/finance/reconciliation/ReconciliationPage';
import { EventSettlementDetailPage } from '../modules/finance/settlement/EventSettlementDetailPage';
import { OrganizerDetailPage } from '../modules/organizers/OrganizerDetailPage';
import { OrganizersListPage } from '../modules/organizers/OrganizersListPage';
import { OrganizerStatementPage } from '../modules/organizers/OrganizerStatementPage';
import { CustomerDetailPage } from '../modules/people/CustomerDetailPage';
import { PeopleAndUsersPage } from '../modules/people/PeopleAndUsersPage';
import { AccreditationPage } from '../modules/people/AccreditationPage';
import { GuestPage } from '../modules/people/GuestPage';
import { UsersPage } from '../modules/people/UsersPage';
import { AuditPage } from '../modules/platform/AuditPage';
import { BackendCapabilitiesPage } from '../modules/platform/BackendCapabilitiesPage';
import { FunctionalityRealityPage } from '../modules/platform/FunctionalityRealityPage';
import { ReleaseNotesPage } from '../modules/platform/ReleaseNotesPage';
import { ReportsMcpPage } from '../modules/platform/ReportsMcpPage';
import { SystemHealthPage } from '../modules/platform/SystemHealthPage';
import { ReportsPage } from '../modules/reports/ReportsPage';
import { RiskPage } from '../modules/risk/RiskPage';
import { AgentDetailPage } from '../modules/sales/AgentDetailPage';
import { BoxOfficeDetailPage } from '../modules/sales/BoxOfficeDetailPage';
import { SalesChannelDetailPage } from '../modules/sales/SalesChannelDetailPage';
import { SalesSetupPage } from '../modules/sales/SalesSetupPage';
import { SettingsPage } from '../modules/settings/SettingsPage';
import { TeamAdminPage } from '../modules/settings/TeamAdminPage';
import { SubscriptionsPage } from '../modules/subscriptions/SubscriptionsPage';
import { SupportPage } from '../modules/support/SupportPage';
import { CallCenterPage } from '../modules/callcenter/CallCenterPage';
import { ChatPage } from '../modules/chat/ChatPage';
import { IssuesApprovalsPage } from '../modules/approvals/IssuesApprovalsPage';
import { UtilitiesPage } from '../modules/utilities/UtilitiesPage';
import { TeamDetailPage } from '../modules/tournaments/TeamDetailPage';
import { TournamentDetailPage } from '../modules/tournaments/TournamentDetailPage';
import { TournamentsPage } from '../modules/tournaments/TournamentsPage';
import { ProductsPage } from '../modules/commerce/ProductsPage';
import { VenueDetailPage } from '../modules/venues/VenueDetailPage';
import { VenuesPage } from '../modules/venues/VenuesPage';

export const Route = createFileRoute('/$')({
  component: LegacySwitch,
  // Pass search params through verbatim so view state (?tab=, filters, page)
  // lives in the URL and a shared link reproduces it — same as every child
  // route had under the old hand-written router.tsx.
  validateSearch: (s) => s,
});

function LegacySwitch() {
  const { authState, sessionUser, callCenter, openDrawer, dashboardChrome, setDashboardChrome, backendStatus, onQuickAction, peopleSegment, globalEventFilter, setGlobalEventFilter } = useShell();

  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { active, id: routeId } = parsePathname(pathname);
  const setActive = (a) => navigate({ to: pathForRoute(a, null) });

  const initEntity = (forActive) => {
    if (active !== forActive || !routeId) return null;
    return readCachedEntity(forActive, routeId) || null;
  };

  const [selectedOrg, setSelectedOrg] = useState(() => initEntity('organizer-detail'));
  const [selectedTxn, setSelectedTxn] = useState(() => initEntity('transaction-detail'));
  const [selectedRequest, setSelectedRequest] = useState(() => initEntity('request-review'));
  const [selectedOrder, setSelectedOrder] = useState(() => initEntity('order-detail'));
  const [selectedCustomer, setSelectedCustomer] = useState(() => initEntity('customer-detail'));
  const [selectedPayout, setSelectedPayout] = useState(() => initEntity('payout-detail'));
  const [selectedSettlement, setSelectedSettlement] = useState(() => initEntity('event-settlement-detail'));
  const [selectedBoxOffice, setSelectedBoxOffice] = useState(() => initEntity('box-office-detail'));
  const [selectedChannel, setSelectedChannel] = useState(() => initEntity('sales-channel-detail'));
  const [selectedAgent, setSelectedAgent] = useState(() => initEntity('agent-detail'));
  const [selectedVenue, setSelectedVenue] = useState(() => initEntity('venue-detail'));
  const [selectedGuest, setSelectedGuest] = useState(() => initEntity('guest-detail'));
  const [selectedTournament, setSelectedTournament] = useState(() => initEntity('tournament-detail'));
  const [selectedTeam, setSelectedTeam] = useState(() => initEntity('team-detail'));
  const [selectedReport, setSelectedReport] = useState(() => initEntity('report-detail'));
  const [failedRouteId, setFailedRouteId] = useState(null); // a detail id that resolved to null (degrade to list)

  // Live mutable state
  const [liveRequests, setLiveRequests] = useState(MOCK.eventRequests);
  const [liveEvents, setLiveEvents] = useState(MOCK.events);

  const mergeRequest = (updated) => {
    setSelectedRequest((current) => current?.id === updated.id ? { ...current, ...updated } : current);
    setLiveRequests(arr => arr.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    invalidateRequestsList();
  };

  const approveRequest = async (reqId, notes) => {
    try {
      if (notes) await addEventRequestNote(reqId, notes);
      const updated = await updateEventRequestStatus(reqId, 'resolved', { resolvedBy: 'Back office' });
      mergeRequest(updated);
      toast('Event request resolved', 'success', `${updated.organizer} · status resolved`);
    } catch (error) {
      toast('Event request update failed', 'error', error?.message || 'Request update failed');
      throw error;
    }
  };

  const rejectRequest = async (reqId, reason) => {
    try {
      if (reason) await addEventRequestNote(reqId, reason);
      const updated = await updateEventRequestStatus(reqId, 'closed', { resolvedBy: 'Back office' });
      mergeRequest(updated);
      toast('Event request closed', 'warning', reason);
    } catch (error) {
      toast('Event request update failed', 'error', error?.message || 'Request update failed');
      throw error;
    }
  };

  const requestMoreInfo = async (reqId, message) => {
    try {
      if (message) await addEventRequestNote(reqId, message);
      const updated = await updateEventRequestStatus(reqId, 'needs_info', { resolvedBy: 'Back office' });
      mergeRequest(updated);
      toast('Info request noted', 'info', 'Status set to needs info — waiting on organizer');
    } catch (error) {
      toast('Event request update failed', 'error', error?.message || 'Request update failed');
      throw error;
    }
  };

  const addRequestNote = async (reqId, note) => {
    try {
      const updated = await addEventRequestNote(reqId, note);
      mergeRequest(updated);
      toast('Request note added', 'success', 'Saved with this request');
      return updated;
    } catch (error) {
      toast('Request note failed', 'error', error?.message || 'Request note failed');
      throw error;
    }
  };

  // Navigation helpers — set the warm entity, cache it for reload, then navigate
  // by URL (the router is the source of truth).
  const navTo = (act, entity) => { cacheEntity(act, entity); navigate({ to: pathForRoute(act, entity) }); };
  // Events are a real route now (see routes/events.$id.tsx) — this just caches +
  // navigates, same as navToRef below, kept under its old name since ~20 other
  // cases in this switch still call it.
  const navToEvent       = (e) => navTo('event-detail', e);
  const navToOrganizer   = (n) => { setSelectedOrg(n);      navTo('organizer-detail', n); };
  const navToOrganizerKyc = (n) => { setSelectedOrg(n); cacheEntity('organizer-detail', n); navigate({ to: pathForRoute('organizer-detail', n), search: { tab: 'kyc' } }); };
  const [selectedStatementOrg, setSelectedStatementOrg] = useState(null);
  const navToOrganizerStatement = (n) => { setSelectedStatementOrg(n); setSelectedOrg(n); navTo('organizer-statement', n); };
  const navToTxn         = (t) => { setSelectedTxn(t);      navTo('transaction-detail', t); };
  const navToRequest     = (r) => { setSelectedRequest(r);  navTo('request-review', r); };
  const navToOrder       = (o) => { setSelectedOrder(o);    navTo('order-detail', o); };
  // No setSelected…/cacheEntity here, unlike its neighbours: the detail page re-fetches by order
  // uuid because it needs the movements and the resolved policy, which the list row does not carry.
  // Seeding it would render a detail with a permanently empty transactions table.
  const navToArrangement = (r) => navigate({ to: pathForRoute('arrangement-detail', { id: r.orderId }) });
  // A customer is only resolvable by phone/email (its real identity — see
  // listCustomers' `routeId` in backOfficeApi.js). A caller building a ref from
  // scratch (an order's buyer, a subscriber row) sometimes has neither — an
  // order placed with only a name on file, say. Rather than a dead click (the
  // old bug: silently landing on the People list) or hiding the affordance,
  // fall back to a SEARCH for whatever partial signal exists — still useful,
  // never a wall. One branch here covers every caller of onNavCustomer.
  const navToCustomer = (c) => {
    if (entityId(c)) { setSelectedCustomer(c); navTo('customer-detail', c); return; }
    const term = c?.name || c?.email || c?.phone || '';
    navigate({ to: pathForRoute('people', null), search: { tab: 'customers', q: term } });
  };
  const navToPayoutDetail= (p) => { setSelectedPayout(p);   navTo('payout-detail', p); };
  const navToSettlementDetail = (s) => { setSelectedSettlement(s); navTo('event-settlement-detail', s); };
  const navToBoxOffice   = (b) => { setSelectedBoxOffice(b); navTo('box-office-detail', b); };
  // No navToChannel: nothing selects a DistributionChannel. The /channels/channel/:id route still
  // resolves by URL (entityById), and the page it renders says why its subject is retired.
  const navToAgent       = (a) => { setSelectedAgent(a);     navTo('agent-detail', a); };
  const navToVenue       = (v) => { setSelectedVenue(v);     navTo('venue-detail', v); };
  const navToGuest       = (g) => { setSelectedGuest(g);     navTo('guest-detail', g); };
  const navToTournament  = (t) => { setSelectedTournament(t);navTo('tournament-detail', t); };
  const navToTeam        = (t) => { setSelectedTeam(t);      navTo('team-detail', t); };

  const navToRef = (act) => (ref) => {
    const id = typeof ref === 'string' ? ref : entityId(ref);
    if (!id) return;
    navigate({ to: pathForRoute(act, { id }) });
  };
  const navToEventRef     = navToRef('event-detail');

  const selectedByActive = {
    'organizer-detail': selectedOrg,
    'organizer-statement': selectedStatementOrg,
    'transaction-detail': selectedTxn,
    'request-review': selectedRequest,
    'order-detail': selectedOrder,
    'customer-detail': selectedCustomer,
    'payout-detail': selectedPayout,
    'event-settlement-detail': selectedSettlement,
    'box-office-detail': selectedBoxOffice,
    'sales-channel-detail': selectedChannel,
    'agent-detail': selectedAgent,
    'venue-detail': selectedVenue,
    'guest-detail': selectedGuest,
    'tournament-detail': selectedTournament,
    'team-detail': selectedTeam,
    // Listed here so /reports/:id resolves cold and a dead id gets DetailNotFound.
    // 'report-run' deliberately is NOT: RunResultDrawer already fetches its own run by id
    // (and that endpoint RE-EXECUTES the report query), so resolving it here too would run
    // an expensive query twice for one link.
    'report-detail': selectedReport,
  };
  const warmEntity = selectedByActive[active] || null;
  const currentEntity = readCachedEntity(active, routeId)
    || (!routeId || String(entityId(warmEntity)) === String(routeId) ? warmEntity : null);

  const applySelectedEntity = (act, obj) => {
    switch (act) {
      case 'organizer-detail': setSelectedOrg(obj); break;
      case 'organizer-statement': setSelectedStatementOrg(obj); break;
      case 'transaction-detail': setSelectedTxn(obj); break;
      case 'request-review': setSelectedRequest(obj); break;
      case 'order-detail': setSelectedOrder(obj); break;
      case 'customer-detail': setSelectedCustomer(obj); break;
      case 'payout-detail': setSelectedPayout(obj); break;
      case 'event-settlement-detail': setSelectedSettlement(obj); break;
      case 'box-office-detail': setSelectedBoxOffice(obj); break;
      case 'sales-channel-detail': setSelectedChannel(obj); break;
      case 'agent-detail': setSelectedAgent(obj); break;
      case 'venue-detail': setSelectedVenue(obj); break;
      case 'guest-detail': setSelectedGuest(obj); break;
      case 'tournament-detail': setSelectedTournament(obj); break;
      case 'team-detail': setSelectedTeam(obj); break;
      case 'report-detail': setSelectedReport(obj); break;
      default: break;
    }
  };

  // Resolve the detail entity for the current URL so a SHARED/COLD link works.
  useEffect(() => {
    if (!routeId) return undefined;
    // DON'T RESOLVE UNTIL THERE IS A SESSION — see original App.jsx history for
    // the false-"doesn't exist" bug this guards against.
    if (authState !== 'authed') return undefined;
    const immediate = readCachedEntity(active, routeId) || getRouteEntity(active, routeId);
    let cancelled = false;
    if (immediate) {
      applySelectedEntity(active, immediate);
      setFailedRouteId(null);
      resolveRouteEntity(active, routeId).then((ent) => {
        if (cancelled || !ent) return;
        applySelectedEntity(active, ent);
        cacheEntity(active, ent);
      });
      return () => { cancelled = true; };
    }
    setFailedRouteId(null);
    resolveRouteEntity(active, routeId).then((ent) => {
      if (cancelled) return;
      if (!ent) { setFailedRouteId(routeId); return; }
      applySelectedEntity(active, ent);
      cacheEntity(active, ent);
    });
    return () => { cancelled = true; };
  }, [active, routeId, authState]);

  const isDetailRoute = Object.prototype.hasOwnProperty.call(selectedByActive, active);
  const detailResolving = !!routeId && isDetailRoute && !currentEntity && failedRouteId !== routeId;

  if (detailResolving) return (
    <div className="flex items-center justify-center py-32 text-[13px] text-[#6B7280]">
      <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…
    </div>
  );
  // NO ROUTE OWNS THIS URL. parsePathname returns active:null rather than quietly answering
  // "dashboard", so a mistyped or dead path says so instead of impersonating the home page —
  // which is what made a routing miss read as "it flashed the dashboard at me".
  if (!active) return <RouteNotFound pathname={pathname} />;

  if (isDetailRoute && routeId && !currentEntity && failedRouteId === routeId) {
    return <DetailNotFound active={active} routeId={routeId} />;
  }

  switch (active) {
    case 'dashboard':       return <DashboardPage setActive={setActive} onQuickAction={onQuickAction} onSelectEvent={navToEvent} onSelectRequest={navToRequest} onSelectOrder={navToOrder} onSelectPayout={navToPayoutDetail} onSelectOrganizerKyc={navToOrganizerKyc} onNavReports={(tab) => navigate({ to: pathForRoute('reports', null), search: { tab: tab || 'analytics' } })} initialDashboard={dashboardChrome} onDashboardLoaded={setDashboardChrome} shellStatus={backendStatus} />;
    case 'organizers':      return <OrganizersListPage onSelect={navToOrganizer} onNavStatement={navToOrganizerStatement} />;
    case 'organizer-statement': return selectedStatementOrg
                                   ? <OrganizerStatementPage org={selectedStatementOrg} onBack={() => navToOrganizer(selectedStatementOrg)} onNavEvent={navToEvent} onNavPayout={navToPayoutDetail} />
                                   : <OrganizersListPage onSelect={navToOrganizer} onNavStatement={navToOrganizerStatement} />;
    case 'organizer-detail': return selectedOrg
                                   ? <OrganizerDetailPage org={selectedOrg} onBack={() => setActive('organizers')} onNavEvent={navToEvent} onNavPayout={navToPayoutDetail} onNavStatement={navToOrganizerStatement} />
                                   : <OrganizersListPage onSelect={navToOrganizer} />;
    case 'event-requests':  return <EventRequestsPage onSelect={navToRequest} liveRequests={liveRequests} rejectRequest={rejectRequest} />;
    case 'request-review':  return selectedRequest
                                   ? <EventRequestReviewPageV2 req={liveRequests.find(r => r.id === selectedRequest.id) || selectedRequest} onBack={() => setActive('event-requests')} onNavOrganizer={navToOrganizer} approveRequest={approveRequest} rejectRequest={rejectRequest} requestMoreInfo={requestMoreInfo} addRequestNote={addRequestNote} navToEvent={navToEvent} liveEvents={liveEvents} />
                                   : <EventRequestsPage onSelect={navToRequest} liveRequests={liveRequests} rejectRequest={rejectRequest} />;
    case 'orders':          return <OrdersPage onSelect={navToOrder} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEventRef} onNavApprovals={() => setActive('issues-approvals')} onNavReports={(e) => { if (e) setGlobalEventFilter(e); navigate({ to: pathForRoute('reports', null), search: { tab: 'analytics' } }); }} />;
    case 'order-detail':    return selectedOrder
                                   ? <OrderDetailPage order={selectedOrder} onBack={() => setActive('orders')} onNavTxn={navToTxn} onNavEvent={navToEvent} onNavCustomer={navToCustomer} />
                                   : <OrdersPage onSelect={navToOrder} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEventRef} onNavApprovals={() => setActive('issues-approvals')} onNavReports={(e) => { if (e) setGlobalEventFilter(e); navigate({ to: pathForRoute('reports', null), search: { tab: 'analytics' } }); }} />;
    case 'people':          return <PeopleAndUsersPage key={`people-${peopleSegment}`} onSelect={navToCustomer} initialSegment={peopleSegment} />;
    case 'accreditation':   return <AccreditationPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onSelectGuest={navToGuest} />;
    case 'guest-detail':    return selectedGuest
                                   ? <GuestPage key={selectedGuest.id} guest={selectedGuest} onBack={() => setActive('accreditation')} onNavEvent={navToEvent} onSelectGuest={navToGuest} />
                                   : <AccreditationPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onSelectGuest={navToGuest} />;
    case 'users':           return <UsersPage onSelect={navToCustomer} />;
    case 'customer-detail': return selectedCustomer
                                   ? <CustomerDetailPage customer={selectedCustomer} onBack={() => setActive('people')} onNavOrder={navToOrder} onNavTxn={navToTxn} onNavEvent={navToEvent} />
                                   : <PeopleAndUsersPage onSelect={navToCustomer} />;
    case 'finance':         return <FinancePage onNavEvent={navToEvent} onNavPayouts={() => setActive('payouts')} actorName={sessionUser?.name || sessionUser?.email} />;
    case 'transactions':    return <TransactionsPageV2 onSelect={navToTxn} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavReports={(e) => { if (e) setGlobalEventFilter(e); navigate({ to: pathForRoute('reports', null), search: { tab: 'platform' } }); }} />;
    case 'transaction-detail': return selectedTxn
                                   ? <TransactionDetailPage txn={selectedTxn} onBack={() => setActive('transactions')} onNavOrder={navToOrder} onNavEvent={navToEvent} onNavOrganizer={navToOrganizer} />
                                   : <TransactionsPageV2 onSelect={navToTxn} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    // Split payments. The detail route needs NO cached-entity fallback of the kind the neighbours
    // above use: an arrangement is addressed by its ORDER uuid, which the API resolves on its own,
    // so a cold deep link and a click from the list take the identical path.
    case 'split-payments':  return <PaymentPlansPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavArrangement={navToArrangement} onNavEvent={(ev) => navigate({ to: pathForRoute('event-splits', { id: ev.id }) })} />;
    case 'arrangement-detail': return <ArrangementDetailPage
                                   arrangementId={routeId}
                                   onBack={() => setActive('split-payments')}
                                   onNavOrder={(d) => navigate({ to: pathForRoute('order-detail', { id: d.orderId }) })}
                                   // Reuses the console's own customer resolution — it falls back to a
                                   // People search when the buyer has no routable id, rather than dead-ending.
                                   onNavCustomer={navToCustomer}
                                   // The EVENT link goes to this module's own event view, not the core
                                   // Events record: from inside Split Payments the question is "what is
                                   // true about splits for this event".
                                   onNavEvent={(ev) => navigate({ to: pathForRoute('event-splits', { id: ev.id }) })}
                                   onNavInventory={(ev) => navigate({ to: pathForRoute('ticketing-inventory', null), search: { event: ev.id } })}
                                   // Carries the arrangement's ORDER CODE, so the ledger opens on
                                   // this plan's own attempts. Without it the only param is the
                                   // ?event= the router retains anyway, and the operator lands on
                                   // the whole event's transactions to go looking for four rows.
                                   onNavTransactions={(reference?: string) => navigate({
                                     to: pathForRoute('transactions', null),
                                     search: (prev: any) => (reference
                                       ? { ...(prev || {}), q: reference, field: 'code' }
                                       : { ...(prev || {}) }),
                                   } as any)}
                                   // ONE payment, not the ledger. A refund is against a specific
                                   // contribution, so the arrangement hands over the movement it
                                   // means. `m.id` IS the PaymentAttempt id (services/arrangements
                                   // `toMovement`), which is exactly what the transaction page keys
                                   // on — so this seeds `selectedTxn` the same way the ledger's own
                                   // row click does, and the page opens without a second lookup.
                                   onNavTransaction={(m) => navToTxn({ txnId: m.id, id: m.id, orderId: m.orderId ?? null, reference: m.reference })}
                                   onNavDefaults={() => navigate({ to: pathForRoute('split-payments', null), search: { tab: 'defaults' } })}
                                 />;
    case 'event-splits': return <EventPaymentPlansPage
                                   eventId={routeId}
                                   onBack={() => navigate({ to: pathForRoute('split-payments', null), search: { tab: 'defaults' } })}
                                   onBackToEvents={() => navigate({ to: pathForRoute('split-payments', null), search: { tab: 'events' } })}
                                   // Hands over to the ONE splits list, scoped — rather than
                                   // growing a second list here with its own filters.
                                   onOpenSplits={(ev) => { setGlobalEventFilter({ id: ev.id, name: ev.title }); navigate({ to: pathForRoute('split-payments', null), search: { tab: 'splits', event: ev.id } }); }}
                                   onOpenEvent={(ev) => navigate({ to: pathForRoute('event-detail', { id: ev.id }) })}
                                 />;
    case 'reconciliation':  return <ReconciliationPage onNavTxn={navToTxn} onNavEvent={navToEvent} />;
    case 'funding':         return <FundingReviewPanel globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'payouts':         return <PayoutsPageV2 onSelect={navToPayoutDetail} onNavSettlement={navToSettlementDetail} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'payout-detail':   return selectedPayout
                                   ? <PayoutDetailPage payout={selectedPayout} onBack={() => setActive('payouts')} onNavOrganizer={navToOrganizer} onNavEvent={navToEvent} onNavTxn={navToTxn} />
                                   : <PayoutsPageV2 onSelect={navToPayoutDetail} onNavSettlement={navToSettlementDetail} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'event-settlement-detail': return selectedSettlement
                                   ? <EventSettlementDetailPage settlement={selectedSettlement} onBack={() => setActive('payouts')} onNavEvent={navToEvent} onNavReconciliation={(runId) => { setActive('reconciliation'); }} />
                                   : <PayoutsPageV2 onSelect={navToPayoutDetail} onNavSettlement={navToSettlementDetail} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'wallets':         return <WalletsPage onSelectWallet={(w) => openDrawer('wallet', w)} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'channels':        return <ChannelsPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavBoxOffice={navToBoxOffice} onNavAgent={navToAgent} />;
    case 'box-office-detail': return selectedBoxOffice
                                   ? <BoxOfficeDetailPage boxOffice={selectedBoxOffice} onBack={() => setActive('channels')} onNavAgent={navToAgent} onNavEvent={navToEvent} />
                                   : <ChannelsPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavBoxOffice={navToBoxOffice} onNavAgent={navToAgent} />;
    case 'sales-channel-detail': return selectedChannel
                                   ? <SalesChannelDetailPage channel={selectedChannel} onBack={() => setActive('channels')} onNavEvent={navToEvent} />
                                   : <ChannelsPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavBoxOffice={navToBoxOffice} onNavAgent={navToAgent} />;
    case 'agent-detail':    return selectedAgent
                                   ? <AgentDetailPage agent={selectedAgent} onBack={() => setActive('channels')} onNavBoxOffice={navToBoxOffice} />
                                   : <ChannelsPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavBoxOffice={navToBoxOffice} onNavAgent={navToAgent} />;
    case 'sales-setup':     return <SalesSetupPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    // Same in/out shape as chat above: the route supplies what is open, the page reports
    // what the operator opened. The page keeps no private idea of which report is showing,
    // so the address bar is always the link you'd send someone.
    case 'reports': case 'report-detail': case 'report-run':
      return <ReportsPage
        globalEventFilter={globalEventFilter}
        setGlobalEventFilter={setGlobalEventFilter}
        onNavEvent={navToEvent}
        routeReport={active === 'report-detail' ? selectedReport : null}
        routeRunId={active === 'report-run' ? routeId : null}
        onOpenReport={(report, opts) => {
          if (report) cacheEntity('report-detail', report);
          navigate({
            to: report ? pathForRoute('report-detail', report) : pathForRoute('reports', null),
            search: (prev) => ({ ...(prev || {}), report: undefined, rtab: opts?.tab || undefined }),
          });
        }}
        onOpenRun={(run) => navigate({
          to: run ? pathForRoute('report-run', run) : pathForRoute('reports', null),
          search: (prev) => ({ ...(prev || {}), report: undefined }),
        })}
      />;
    case 'venues':          return <VenuesPage onSelect={navToVenue} />;
    case 'venue-detail':    return selectedVenue
                                   ? <VenueDetailPage venue={selectedVenue} onBack={() => setActive('venues')} onNavEvent={navToEvent} onNavTournament={navToTournament} />
                                   : <VenuesPage onSelect={navToVenue} />;
    case 'access-control':  return <AccessControlPage onNavEvent={navToEvent} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavReports={(e) => { if (e) setGlobalEventFilter(e); navigate({ to: pathForRoute('reports', null), search: { tab: 'library' } }); }} />;
    case 'ticketing-inventory': return <TicketingInventoryPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavOrder={navToOrder} onNavEvent={navToEvent} liveEvents={liveEvents} />;
    case 'subscriptions':       return <SubscriptionsPage      globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavCustomer={navToCustomer} liveEvents={liveEvents} />;
    case 'tournaments':     return <TournamentsPage onSelect={navToTournament} onSelectTeam={navToTeam} userEmail={sessionUser?.email} />;
    case 'season-passes':   return <TournamentsPage onSelect={navToTournament} onSelectTeam={navToTeam} defaultTab="Season passes" userEmail={sessionUser?.email} />;
    case 'products':        return <ProductsPage />;
    case 'tournament-detail': return selectedTournament
                                   ? <TournamentDetailPage tournament={selectedTournament} onBack={() => setActive('tournaments')} onNavVenue={navToVenue} onNavEvent={navToEvent} onNavTeam={navToTeam} />
                                   : <TournamentsPage onSelect={navToTournament} onSelectTeam={navToTeam} userEmail={sessionUser?.email} />;
    case 'team-detail':     return selectedTeam
                                   ? <TeamDetailPage team={selectedTeam} onBack={() => setActive('tournaments')} onNavTournament={navToTournament} />
                                   : <TournamentsPage onSelect={navToTournament} onSelectTeam={navToTeam} userEmail={sessionUser?.email} />;
    case 'support':         return <SupportPage onNavOrder={navToOrder} onNavEvent={navToEvent} />;
    case 'call-center':     return <CallCenterPage callCenter={callCenter} />;
    case 'chat': case 'chat-thread':
      return <ChatPage
        onNavOrder={navToOrder}
        sessionUser={sessionUser}
        initialContact={active === 'chat-thread' ? routeId : null}
        onSelectContact={(contact) => navigate({ to: contact ? pathForRoute('chat-thread', { id: contact }) : pathForRoute('chat', null) })}
      />;
    case 'issues-approvals': return <IssuesApprovalsPage onBack={() => setActive('orders')} onNavOrder={(r) => navToOrder({ id: r.orderId, routeId: r.orderId })} />;
    case 'design':          return <DesignPage onSelect={(t) => openDrawer('template', t)} globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} />;
    case 'risk':            return <RiskPage />;
    case 'audit':           return <AuditPage globalEventFilter={globalEventFilter} setGlobalEventFilter={setGlobalEventFilter} onNavEvent={navToEvent} onNavigate={setActive} />;
    case 'utilities':       return <UtilitiesPage />;
    case 'backend-capabilities': return <BackendCapabilitiesPage />;
    case 'functionality-reality': return <FunctionalityRealityPage />;
    case 'reports-mcp':     return <ReportsMcpPage />;
    case 'release-notes':   return <ReleaseNotesPage />;
    case 'settings':        return <SettingsPage />;
    /**
     * `/team` USED TO RENDER A SURFACE THAT LIED.
     *
     * TeamAdminPage holds `staff` and `roles` in local state and fires six success toasts that
     * assert persistence — "Custom access saved", "Role created", "Role deleted", and worst,
     * "Invite sent", which tells an operator an email reached a colleague. Nothing was ever sent
     * anywhere. functionalityReality.js records it as "dead/orphaned… safe to delete", but that is
     * not quite right: it was NOT in the nav, yet it WAS routed here, so a bookmark or a shared
     * link reached it and every button appeared to work.
     *
     * Real role management already exists — RolesPermissionsPage, reading /api/admin/roles, inside
     * Settings. So the route now lands there instead of 404ing a URL somebody may have saved:
     * the lie is unreachable and no capability is lost. The component is left in the tree
     * unreferenced (see its header) rather than deleted, so the UI work survives if anyone wires
     * it for real.
     */
    case 'team':            return <SettingsPage />;
    case 'health':          return <SystemHealthPage />;
    default:                return <DashboardPage setActive={setActive} onQuickAction={onQuickAction} initialDashboard={dashboardChrome} onDashboardLoaded={setDashboardChrome} shellStatus={backendStatus} />;
  }
}
